import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
} from 'react'

interface DecryptedTextProps extends HTMLAttributes<HTMLSpanElement> {
  text: string
  speed?: number
  maxIterations?: number
  sequential?: boolean
  revealDirection?: 'start' | 'end' | 'center'
  useOriginalCharsOnly?: boolean
  characters?: string
  className?: string
  parentClassName?: string
  encryptedClassName?: string
  animateOn?: 'view' | 'hover' | 'inViewHover' | 'click'
  clickMode?: 'once' | 'toggle'
}

const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  border: 0,
}

const wrapperStyle: React.CSSProperties = {
  display: 'inline-block',
  whiteSpace: 'pre-wrap',
}

type Direction = 'forward' | 'reverse'

export default function DecryptedText({
  text,
  speed = 50,
  maxIterations = 10,
  sequential = false,
  revealDirection = 'start',
  useOriginalCharsOnly = false,
  characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+',
  className = '',
  parentClassName = '',
  encryptedClassName = '',
  animateOn = 'hover',
  clickMode = 'once',
  ...props
}: DecryptedTextProps) {
  const [displayText, setDisplayText] = useState<string>(text)
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set())
  const [hasAnimated, setHasAnimated] = useState<boolean>(false)
  const [isDecrypted, setIsDecrypted] = useState<boolean>(animateOn !== 'click')
  const [direction, setDirection] = useState<Direction>('forward')

  const containerRef = useRef<HTMLSpanElement>(null)
  const orderRef = useRef<number[]>([])
  const pointerRef = useRef<number>(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const availableChars = useMemo<string[]>(() => {
    return useOriginalCharsOnly
      ? Array.from(new Set(text.split(''))).filter((c) => c !== ' ')
      : characters.split('')
  }, [useOriginalCharsOnly, text, characters])

  const shuffleText = useCallback(
    (originalText: string, currentRevealed: Set<number>) => {
      return originalText
        .split('')
        .map((char, i) => {
          if (char === ' ') return ' '
          if (currentRevealed.has(i)) return originalText[i]
          return availableChars[Math.floor(Math.random() * availableChars.length)]
        })
        .join('')
    },
    [availableChars]
  )

  const computeOrder = useCallback(
    (len: number): number[] => {
      const order: number[] = []
      if (len <= 0) return order
      if (revealDirection === 'start') {
        for (let i = 0; i < len; i++) order.push(i)
        return order
      }
      if (revealDirection === 'end') {
        for (let i = len - 1; i >= 0; i--) order.push(i)
        return order
      }
      const middle = Math.floor(len / 2)
      let offset = 0
      while (order.length < len) {
        if (offset % 2 === 0) {
          const idx = middle + offset / 2
          if (idx >= 0 && idx < len) order.push(idx)
        } else {
          const idx = middle - Math.ceil(offset / 2)
          if (idx >= 0 && idx < len) order.push(idx)
        }
        offset++
      }
      return order.slice(0, len)
    },
    [revealDirection]
  )

  const fillAllIndices = useCallback((): Set<number> => {
    const s = new Set<number>()
    for (let i = 0; i < text.length; i++) s.add(i)
    return s
  }, [text])

  const removeRandomIndices = useCallback(
    (set: Set<number>, count: number): Set<number> => {
      const arr = Array.from(set)
      for (let i = 0; i < count && arr.length > 0; i++) {
        const idx = Math.floor(Math.random() * arr.length)
        arr.splice(idx, 1)
      }
      return new Set(arr)
    },
    []
  )

  const encryptInstantly = useCallback(() => {
    const empty = new Set<number>()
    setRevealedIndices(empty)
    setDisplayText(shuffleText(text, empty))
    setIsDecrypted(false)
  }, [text, shuffleText])

  const triggerDecrypt = useCallback(() => {
    if (sequential) {
      orderRef.current = computeOrder(text.length)
      pointerRef.current = 0
    }
    setRevealedIndices(new Set())
    setDirection('forward')
    setIsAnimating(true)
  }, [sequential, computeOrder, text.length])

  const triggerReverse = useCallback(() => {
    if (sequential) {
      orderRef.current = computeOrder(text.length).slice().reverse()
      pointerRef.current = 0
    }
    setRevealedIndices(fillAllIndices())
    setDisplayText(shuffleText(text, fillAllIndices()))
    setDirection('reverse')
    setIsAnimating(true)
  }, [sequential, computeOrder, fillAllIndices, shuffleText, text])

  useEffect(() => {
    if (!isAnimating) return

    let currentIteration = 0

    const getNextIndex = (revealedSet: Set<number>): number => {
      const len = text.length
      switch (revealDirection) {
        case 'start':
          return revealedSet.size
        case 'end':
          return len - 1 - revealedSet.size
        case 'center': {
          const middle = Math.floor(len / 2)
          const offset = Math.floor(revealedSet.size / 2)
          const next = revealedSet.size % 2 === 0 ? middle + offset : middle - offset - 1
          if (next >= 0 && next < len && !revealedSet.has(next)) return next
          for (let i = 0; i < len; i++) if (!revealedSet.has(i)) return i
          return 0
        }
        default:
          return revealedSet.size
      }
    }

    intervalRef.current = setInterval(() => {
      setRevealedIndices((prev) => {
        if (sequential) {
          if (direction === 'forward') {
            if (prev.size < text.length) {
              const next = getNextIndex(prev)
              const newSet = new Set(prev)
              newSet.add(next)
              setDisplayText(shuffleText(text, newSet))
              return newSet
            }
            clearInterval(intervalRef.current ?? undefined)
            setIsAnimating(false)
            setIsDecrypted(true)
            return prev
          }
          // reverse sequential
          if (pointerRef.current < orderRef.current.length) {
            const idx = orderRef.current[pointerRef.current++]
            const newSet = new Set(prev)
            newSet.delete(idx)
            setDisplayText(shuffleText(text, newSet))
            if (newSet.size === 0) {
              clearInterval(intervalRef.current ?? undefined)
              setIsAnimating(false)
              setIsDecrypted(false)
            }
            return newSet
          }
          clearInterval(intervalRef.current ?? undefined)
          setIsAnimating(false)
          setIsDecrypted(false)
          return prev
        }

        // non-sequential
        if (direction === 'forward') {
          setDisplayText(shuffleText(text, prev))
          currentIteration++
          if (currentIteration >= maxIterations) {
            clearInterval(intervalRef.current ?? undefined)
            setIsAnimating(false)
            setDisplayText(text)
            setIsDecrypted(true)
          }
          return prev
        }
        // non-sequential reverse
        let currentSet = prev
        if (currentSet.size === 0) currentSet = fillAllIndices()
        const removeCount = Math.max(1, Math.ceil(text.length / Math.max(1, maxIterations)))
        const next = removeRandomIndices(currentSet, removeCount)
        setDisplayText(shuffleText(text, next))
        currentIteration++
        if (next.size === 0 || currentIteration >= maxIterations) {
          clearInterval(intervalRef.current ?? undefined)
          setIsAnimating(false)
          setIsDecrypted(false)
          setDisplayText(shuffleText(text, new Set()))
          return new Set()
        }
        return next
      })
    }, speed)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [
    isAnimating,
    text,
    speed,
    maxIterations,
    sequential,
    revealDirection,
    shuffleText,
    direction,
    fillAllIndices,
    removeRandomIndices,
  ])

  const handleClick = () => {
    if (animateOn !== 'click') return
    if (clickMode === 'once') {
      if (isDecrypted) return
      setDirection('forward')
      triggerDecrypt()
    }
    if (clickMode === 'toggle') {
      if (isDecrypted) triggerReverse()
      else {
        setDirection('forward')
        triggerDecrypt()
      }
    }
  }

  const triggerHoverDecrypt = useCallback(() => {
    if (isAnimating) return
    setRevealedIndices(new Set())
    setIsDecrypted(false)
    setDisplayText(text)
    setDirection('forward')
    setIsAnimating(true)
  }, [isAnimating, text])

  const resetToPlainText = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setIsAnimating(false)
    setRevealedIndices(new Set())
    setDisplayText(text)
    setIsDecrypted(true)
    setDirection('forward')
  }, [text])

  // Trigger on view
  useEffect(() => {
    if (animateOn !== 'view' && animateOn !== 'inViewHover') return
    const cb = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasAnimated) {
          triggerDecrypt()
          setHasAnimated(true)
        }
      })
    }
    const observer = new IntersectionObserver(cb, { threshold: 0.1 })
    const node = containerRef.current
    if (node) observer.observe(node)
    return () => {
      if (node) observer.unobserve(node)
    }
  }, [animateOn, hasAnimated, triggerDecrypt])

  // Initial state when animateOn changes
  useEffect(() => {
    if (animateOn === 'click') {
      encryptInstantly()
    } else if (animateOn === 'view' || animateOn === 'inViewHover') {
      // start scrambled, IntersectionObserver will trigger reveal
      const empty = new Set<number>()
      setRevealedIndices(empty)
      setDisplayText(shuffleText(text, empty))
      setIsDecrypted(false)
    } else {
      setDisplayText(text)
      setIsDecrypted(true)
    }
    setDirection('forward')
  }, [animateOn, text, encryptInstantly, shuffleText])

  const handlers =
    animateOn === 'hover' || animateOn === 'inViewHover'
      ? { onMouseEnter: triggerHoverDecrypt, onMouseLeave: resetToPlainText }
      : animateOn === 'click'
        ? { onClick: handleClick }
        : {}

  return (
    <span
      ref={containerRef}
      className={parentClassName}
      style={wrapperStyle}
      {...handlers}
      {...props}
    >
      <span style={srOnly}>{text}</span>
      <span aria-hidden="true">
        {displayText.split('').map((char, i) => {
          const revealed = revealedIndices.has(i) || (!isAnimating && isDecrypted)
          return (
            <span key={i} className={revealed ? className : encryptedClassName}>
              {char}
            </span>
          )
        })}
      </span>
    </span>
  )
}
